import React, { useState, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    Decimation
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import crosshairPlugin from "chartjs-plugin-crosshair";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    Decimation,
    zoomPlugin,
    crosshairPlugin
);

// Colors
const PRICE_COLOR = 'green';  // green for S&P 500 price line
const HIGHLIGHT_COLOR = 'rgba(255, 0, 0, 0.7)';  // red for similar dates

// RV-IV gradient coloring: interpolate from blue (negative/expensive) to red (positive/cheap)
// Range: -30 bps (most blue) to +30 bps (most red), with gray at 0
const getRVIVColor = (value) => {
    if (value === null || value === undefined) return 'rgb(150, 150, 150)';
    const clampedVal = Math.max(-30, Math.min(30, value));
    const t = (clampedVal + 30) / 60;
    let r, g, b;
    if (t < 0.5) {
        const localT = t * 2;
        r = Math.round(100 + (150 - 100) * localT);
        g = Math.round(140 + (150 - 140) * localT);
        b = Math.round(200 + (150 - 200) * localT);
    } else {
        const localT = (t - 0.5) * 2;
        r = Math.round(150 + (200 - 150) * localT);
        g = Math.round(150 + (100 - 150) * localT);
        b = Math.round(150 + (100 - 150) * localT);
    }
    return `rgb(${r}, ${g}, ${b})`;
};

const RVIVTimeSeriesChart = ({ thresholdBps = 2 }) => {
    const [data, setData] = useState([]);
    const [timestamp, setTimestamp] = useState("");
    const [loading, setLoading] = useState(true);
    const chartRef = useRef(null);

    const API_URL = process.env.REACT_APP_API_URL || "";

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${API_URL}/data/volatility`);
                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

                const { data: jsonData, timestamp: fileTimestamp } = await response.json();
                setData(jsonData);
                setTimestamp(fileTimestamp);
            } catch (error) {
                console.error("Error fetching volatility data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [API_URL]);

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>;
    }

    if (data.length === 0) {
        return <div style={{ textAlign: 'center', padding: '20px' }}>No data available</div>;
    }

    // Get latest RV-IV value
    const latestRow = data[data.length - 1];
    const latestRVIV = latestRow?.rv_iv;
    const rvivLower = latestRVIV - thresholdBps;
    const rvivUpper = latestRVIV + thresholdBps;

    // Find indices where RV-IV aligns with current (excluding latest)
    const similarIndices = new Set();
    for (let i = 0; i < data.length - 1; i++) {
        const rv_iv = data[i].rv_iv;
        if (rv_iv !== null && rv_iv >= rvivLower && rv_iv <= rvivUpper) {
            similarIndices.add(i);
        }
    }

    // Format data as {x, y} for decimation plugin (requires parsing: false)
    const closeData = data.map((d, i) => ({ x: i, y: d.close }));
    const rvivData = data.map((d, i) => ({ x: i, y: d.rv_iv }));
    const highlightData = data.map((d, i) =>
        similarIndices.has(i) ? { x: i, y: d.close } : { x: i, y: null }
    );
    // Zero line for RV-IV axis
    const zeroLineData = [{ x: 0, y: 0 }, { x: data.length - 1, y: 0 }];
    const labels = data.map(d => d.date);

    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    const twoYearsAgoStr = twoYearsAgo.toISOString().split('T')[0];
    const startYear2022Idx = data.findIndex(d => d.date >= twoYearsAgoStr);
    const initialMinIdx = startYear2022Idx >= 0 ? startYear2022Idx : 0;

    const chartData = {
        labels: labels,
        datasets: [
            {
                label: "S&P 500 Close",
                data: closeData,
                borderColor: PRICE_COLOR,
                backgroundColor: PRICE_COLOR,
                borderWidth: 1.5,
                tension: 0,
                fill: false,
                yAxisID: "y",
                pointRadius: 0,
                pointHitRadius: 0,
                order: 3,
            },
            {
                label: "Similar Dates",
                data: highlightData,
                borderColor: HIGHLIGHT_COLOR,
                backgroundColor: HIGHLIGHT_COLOR,
                borderWidth: 0,
                fill: false,
                yAxisID: "y",
                pointRadius: 4,
                pointStyle: 'circle',
                showLine: false,
                spanGaps: false,
                order: 1,
            },
            {
                label: "RV-IV (bps)",
                data: rvivData,
                borderColor: 'rgb(150, 150, 150)',
                backgroundColor: 'rgb(150, 150, 150)',
                borderWidth: 2,
                tension: 0,
                fill: false,
                yAxisID: "y1",
                pointRadius: 0,
                pointHitRadius: 0,
                order: 2,
                segment: {
                    borderColor: (ctx) => getRVIVColor(ctx.p1.parsed.y)
                }
            },
            {
                label: "Zero Line",
                data: zeroLineData,
                borderColor: 'rgba(0, 0, 0, 0.5)',
                borderWidth: 1,
                borderDash: [5, 5],
                tension: 0,
                fill: false,
                yAxisID: "y1",
                pointRadius: 0,
                order: 4,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        parsing: false,
        normalized: true,
        plugins: {
            decimation: {
                enabled: true,
                algorithm: 'lttb',
                samples: 500,
            },
            legend: {
                position: "top",
                labels: {
                    font: { size: window.innerWidth < 600 ? 10 : 12 },
                    usePointStyle: true,
                    filter: (legendItem) => {
                        if (legendItem.text === "Zero Line") return false;
                        if (legendItem.text === "Similar Dates" && similarIndices.size === 0) {
                            return false;
                        }
                        return true;
                    }
                },
            },
            title: {
                display: false,
            },
            tooltip: {
                mode: "index",
                intersect: false,
                position: "nearest",
                callbacks: {
                    title: (items) => {
                        if (items.length > 0) {
                            const idx = Math.round(items[0].raw?.x ?? items[0].dataIndex);
                            if (idx >= 0 && idx < labels.length) {
                                return labels[idx];
                            }
                        }
                        return '';
                    },
                    label: (context) => {
                        const value = context.raw?.y ?? context.raw;
                        if (value === null || value === undefined) return null;
                        if (context.dataset.label === "S&P 500 Close") {
                            return `Close: $${value.toFixed(2)}`;
                        } else if (context.dataset.label === "RV-IV (bps)") {
                            return `RV-IV: ${value.toFixed(0)} bps`;
                        } else if (context.dataset.label === "Similar Dates") {
                            const idx = Math.round(context.raw?.x ?? context.dataIndex);
                            const row = data[idx];
                            const vt = row?.vol_trend != null ? row.vol_trend.toFixed(2) : 'n/a';
                            return `Similar (RV-IV: ${row?.rv_iv?.toFixed(0)} bps, Trend: ${vt})`;
                        }
                        return `${context.dataset.label}: ${value}`;
                    },
                    filter: (tooltipItem) => {
                        if (tooltipItem.dataset.label === "Zero Line") return false;
                        const val = tooltipItem.raw?.y ?? tooltipItem.raw;
                        return val !== null && val !== undefined;
                    },
                },
            },
            zoom: {
                pan: { enabled: true, mode: "x" },
                zoom: {
                    wheel: { enabled: true, speed: 0.09 },
                    pinch: { enabled: true, speed: 0.09 },
                    mode: "x"
                },
            },
            crosshair: {
                line: { color: "rgba(0, 0, 0, 0.2)", width: 1 },
                sync: { enabled: false },
                zoom: { enabled: false },
            },
        },
        interaction: { mode: "nearest", intersect: false, axis: "x" },
        scales: {
            x: {
                type: 'linear',
                min: initialMinIdx,
                max: data.length - 1,
                title: { display: true, text: "Date" },
                ticks: {
                    autoSkip: true,
                    maxTicksLimit: 10,
                    font: { size: 11 },
                    callback: function(value) {
                        const idx = Math.round(value);
                        if (idx >= 0 && idx < labels.length) {
                            return labels[idx];
                        }
                        return '';
                    }
                },
            },
            y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "S&P 500 Close ($)" },
                grid: { drawOnChartArea: true, color: "rgba(200, 200, 200, 0.2)" },
            },
            y1: {
                type: "linear",
                position: "right",
                title: { display: true, text: "RV-IV (bps)" },
                grid: { drawOnChartArea: false },
            },
        },
    };

    const resetZoom = () => {
        if (chartRef.current) {
            chartRef.current.resetZoom();
        }
    };

    return (
        <div style={{ width: '100%' }}>
            <div style={{ height: '400px' }}>
                <Line ref={chartRef} data={chartData} options={options} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', padding: '8px 0' }}>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    Updated: {timestamp}
                </span>
                <button
                    onClick={resetZoom}
                    style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        backgroundColor: '#4a90d9',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Reset Zoom
                </button>
            </div>
        </div>
    );
};

export default RVIVTimeSeriesChart;